import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FaUsers, FaClipboardList, FaCalendarAlt, FaChartLine, FaComments } from 'react-icons/fa';
import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';

const PageContainer = styled.div`
  padding: 1rem;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const Title = styled.h1`
  margin-bottom: 0.75rem;
  font-size: 1.8rem;
  
  @media (min-width: 768px) {
    margin-bottom: 1rem;
    font-size: 2.2rem;
  }
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 1.5rem;
  font-size: 0.95rem;
  
  @media (min-width: 768px) {
    margin-bottom: 2rem;
    font-size: 1rem;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;
  
  @media (min-width: 576px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (min-width: 992px) {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 1.5rem;
    margin-bottom: 2rem;
  }
`;

const StatCard = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.25rem;
  
  @media (min-width: 768px) {
    padding: 1.5rem;
  }
`;

const StatIcon = styled.div`
  font-size: 1.75rem;
  margin-bottom: 0.75rem;
  color: ${({ theme }) => theme.colors.primary};
  
  @media (min-width: 768px) {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
`;

const StatValue = styled.div`
  font-size: 1.75rem;
  font-weight: bold;
  margin-bottom: 0.3rem;
  
  @media (min-width: 768px) {
    font-size: 2rem;
    margin-bottom: 0.5rem;
  }
`;

const StatLabel = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
  font-size: 0.9rem;
  
  @media (min-width: 768px) {
    font-size: 1rem;
  }
`;

const SectionTitle = styled.h2`
  margin-bottom: 0.75rem;
  margin-top: 1.5rem;
  font-size: 1.3rem;
  
  @media (min-width: 768px) {
    margin-bottom: 1rem;
    margin-top: 2rem;
    font-size: 1.5rem;
  }
`;

const ModulesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;
  
  @media (min-width: 576px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (min-width: 992px) {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 1.5rem;
    margin-bottom: 2rem;
  }
`;

const ModuleCard = styled(Card)`
  height: 100%;
`;

const PendingApprovalContainer = styled.div`
  background-color: #fff3cd;
  border: 1px solid #ffeeba;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  
  @media (min-width: 768px) {
    padding: 1.5rem;
    margin-bottom: 2rem;
  }
`;

const PendingTitle = styled.h3`
  color: #856404;
  margin-bottom: 0.5rem;
  font-size: 1.1rem;
  
  @media (min-width: 768px) {
    margin-bottom: 0.75rem;
    font-size: 1.2rem;
  }
`;

const PendingText = styled.p`
  color: #856404;
  margin-bottom: 0.3rem;
  font-size: 0.9rem;
  
  @media (min-width: 768px) {
    margin-bottom: 0.5rem;
    font-size: 1rem;
  }
`;

const RecentClientsList = styled.div`
  margin-top: 1rem;
  
  @media (min-width: 768px) {
    margin-top: 1.5rem;
  }
`;

const ClientItem = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0.75rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  
  @media (min-width: 576px) {
    flex-direction: row;
    align-items: center;
    padding: 1rem;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const ClientInfo = styled.div`
  flex-grow: 1;
  margin-bottom: 0.5rem;
  
  @media (min-width: 576px) {
    margin-bottom: 0;
  }
`;

const ClientName = styled.div`
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const ClientDetail = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.9rem;
`;

const LoadingMessage = styled.p`
  text-align: center;
  padding: 1.5rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  
  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const PartnerDashboard = () => {
  const { currentUser } = useAuth();
  const [partnerData, setPartnerData] = useState(null);
  const [clientsData, setClientsData] = useState([]);
  const [stats, setStats] = useState({
    totalClients: 0,
    activePlans: 0,
    monthlyRevenue: 0
  });
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchPartnerData = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        
        // Fetch partner info
        const partnerRef = doc(db, 'partners', currentUser.uid);
        const partnerDoc = await getDoc(partnerRef);
        
        if (partnerDoc.exists()) {
          setPartnerData(partnerDoc.data());
          
          // If partner is approved, fetch clients data
          if (!partnerDoc.data().pendingApproval) {
            // Get clients linked to this partner
            const clientsQuery = query(
              collection(db, 'partners', currentUser.uid, 'clients')
            );
            const clientsSnapshot = await getDocs(clientsQuery);
            
            const clientIds = clientsSnapshot.docs.map(doc => doc.id);
            const clientsData = [];
            let totalPlans = 0;
            
            // Fetch each client's info
            for (const clientId of clientIds) {
              const clientDoc = await getDoc(doc(db, 'users', clientId));
              if (clientDoc.exists()) {
                clientsData.push({
                  id: clientId,
                  ...clientDoc.data()
                });
              }
              
              // Count plans per client
              const plansQuery = query(
                collection(db, 'users', clientId, 'plans')
              );
              const plansSnapshot = await getDocs(plansQuery);
              totalPlans += plansSnapshot.size;
            }
            
            setClientsData(clientsData);
            
            // Set stats
            setStats({
              totalClients: clientIds.length,
              activePlans: totalPlans,
              monthlyRevenue: clientIds.length * 100 // Placeholder: $100 per client
            });
          }
        }
      } catch (err) {
        console.error('Error fetching partner data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPartnerData();
  }, [currentUser]);
  
  // Show loading state
  if (loading) {
    return (
      <PageContainer>
        <LoadingMessage>Loading dashboard data...</LoadingMessage>
      </PageContainer>
    );
  }
  
  // If partner account is pending approval, show a message
  if (partnerData && partnerData.pendingApproval) {
    return (
      <PageContainer>
        <Title>Partner Dashboard</Title>
        <Subtitle>Welcome to Geo Diet Wonder!</Subtitle>
        
        <PendingApprovalContainer>
          <PendingTitle>Your Account is Pending Approval</PendingTitle>
          <PendingText>Thank you for registering as a healthcare partner. Your account is currently under review by our admin team.</PendingText>
          <PendingText>You will receive an email notification once your account has been approved. This typically takes 1-2 business days.</PendingText>
        </PendingApprovalContainer>
        
        <Card>
          <Card.Header>Next Steps</Card.Header>
          <Card.Content>
            <p>While you wait for approval, you can:</p>
            <ul>
              <li>Complete your profile information</li>
              <li>Review our partner guidelines</li>
              <li>Explore our platform features</li>
            </ul>
          </Card.Content>
          <Card.Footer>
            <Button as={Link} to="/partner/profile" variant="outline" fullWidth>
              Complete Your Profile
            </Button>
          </Card.Footer>
        </Card>
      </PageContainer>
    );
  }
  
  return (
    <PageContainer>
      <Title>Partner Dashboard</Title>
      <Subtitle>Welcome back{partnerData?.displayName ? `, ${partnerData.displayName}` : ''}! Here's an overview of your partner account.</Subtitle>
      
      <StatsGrid>
        <StatCard>
          <StatIcon>
            <FaUsers />
          </StatIcon>
          <StatValue>{stats.totalClients}</StatValue>
          <StatLabel>Active Clients</StatLabel>
        </StatCard>
        
        <StatCard>
          <StatIcon>
            <FaClipboardList />
          </StatIcon>
          <StatValue>{stats.activePlans}</StatValue>
          <StatLabel>Active Plans</StatLabel>
        </StatCard>
        
        <StatCard>
          <StatIcon>
            <FaChartLine />
          </StatIcon>
          <StatValue>${stats.monthlyRevenue}</StatValue>
          <StatLabel>Monthly Revenue</StatLabel>
        </StatCard>
      </StatsGrid>
      
      {clientsData.length > 0 && (
        <>
          <SectionTitle>Recent Clients</SectionTitle>
          <Card>
            <RecentClientsList>
              {clientsData.slice(0, 5).map(client => (
                <ClientItem key={client.id}>
                  <ClientInfo>
                    <ClientName>{client.displayName || 'Unnamed Client'}</ClientName>
                    <ClientDetail>{client.email}</ClientDetail>
                  </ClientInfo>
                  <Button 
                    as={Link} 
                    to={`/partner/clients/${client.id}`} 
                    variant="outline" 
                    size="small"
                  >
                    View
                  </Button>
                </ClientItem>
              ))}
              
              {clientsData.length > 5 && (
                <Card.Footer>
                  <Button as={Link} to="/partner/clients" variant="outline">
                    View All Clients
                  </Button>
                </Card.Footer>
              )}
            </RecentClientsList>
          </Card>
        </>
      )}
    </PageContainer>
  );
};

export default PartnerDashboard; 